import { useEffect, useId } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { Spinner } from '@/shared/ui/spinner'

export type DocumentRenameDialogProps = {
  /** El documento a renombrar, o `null` con el diálogo cerrado. */
  document: { id: string; name: string } | null
  onOpenChange: (open: boolean) => void
  pending: boolean
  onSubmit: (name: string) => void
}

/**
 * CAMBIAR EL NOMBRE VISIBLE de un documento ya subido.
 *
 * ## El hueco que cierra
 *
 * ☠️ El endpoint —`PATCH /v2/admin/documents/:id/name`— existía desde la tajada del crédito y
 * **sólo lo usaba Créditos**. En el cartapacio, un `escaneo_final_v3(1).pdf` mal nombrado no se
 * podía corregir: la única salida era borrarlo y volver a subirlo, que cuesta la bitácora del
 * adjunto, su `assetId`, y —si ya estaba publicado— su fecha de publicación. La puerta estaba
 * abierta; faltaba el botón.
 *
 * ## Sólo el nombre
 *
 * Ni comentario, ni carpeta, ni categoría: cada uno tiene su puerta con su permiso, y un
 * formulario con seis campos opcionales es el lugar donde el permiso más flojo termina
 * gobernando al más estricto. El nombre no cambia quién ve el archivo — por eso puede editarse
 * desde acá sin confirmación ni alcance.
 *
 * ## El campo llega con el nombre puesto y SELECCIONADO
 *
 * Renombrar casi siempre es reemplazar, no editar: quien abre esto ya tiene el nombre nuevo en
 * la cabeza. Con el texto seleccionado, escribir lo pisa; sin eso hay que borrar veinte
 * caracteres antes de empezar. `autoFocus` está justificado acá y sólo acá — es un diálogo con
 * un único campo, abierto por una acción explícita.
 */
export function DocumentRenameDialog({
  document,
  onOpenChange,
  pending,
  onSubmit
}: DocumentRenameDialogProps) {
  const inputId = useId()
  const { control, handleSubmit, reset } = useForm<{ name: string }>({
    defaultValues: { name: '' }
  })

  // Al ABRIR y no al cerrar, por lo mismo que el diálogo de subida: el cierre del final feliz lo
  // hace el padre y no pasa por `onOpenChange`, así que resetear ahí dejaría el nombre viejo
  // puesto la próxima vez.
  useEffect(() => {
    if (!document) return
    reset({ name: document.name })
  }, [document, reset])

  return (
    <Dialog
      open={Boolean(document)}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar nombre</DialogTitle>
          <DialogDescription>
            Cambia solo la etiqueta con la que se lee el documento. El archivo, su historial y quién
            puede verlo no cambian.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => {
            const name = values.name.trim()
            if (!name) return
            onSubmit(name)
          })}
        >
          <Controller
            control={control}
            name="name"
            rules={{
              required: 'El nombre no puede quedar vacío.',
              maxLength: { value: 200, message: 'Máximo 200 caracteres.' }
            }}
            render={({ field, fieldState }) => (
              <Field data-invalid={Boolean(fieldState.error)}>
                <FieldLabel htmlFor={inputId}>Nombre visible</FieldLabel>
                <Input
                  id={inputId}
                  autoComplete="off"
                  spellCheck={false}
                  // `autoFocus` está justificado acá y sólo acá: es un diálogo de UN campo,
                  // abierto por una acción explícita. Es el caso que la guía admite, y sin él
                  // renombrar pide un click extra en el único lugar donde se puede escribir.
                  autoFocus
                  onFocus={(event) => {
                    event.currentTarget.select()
                  }}
                  {...field}
                />
                <FieldDescription>
                  Así lo va a leer el cliente en su app si está publicado.
                </FieldDescription>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <Spinner className="h-4 w-4" /> : null}
              Guardar nombre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
